import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sanitizeInput } from '../../services/securityService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import toast from 'react-hot-toast';

const FEATURES = [
  {
    icon: 'Infinity',
    title: 'Unlimited Case Volume',
    desc: 'No caps, no overages. Process thousands of cases per month with consistent pricing.',
  },
  {
    icon: 'Shield',
    title: 'Dedicated Security & Compliance',
    desc: 'SOC 2-ready infrastructure, custom data retention policies, and NFPA 921 alignment.',
  },
  {
    icon: 'Users',
    title: 'Multi-Seat Team Access',
    desc: 'Role-based access control for investigators, analysts, and supervisors across departments.',
  },
  {
    icon: 'LifeBuoy',
    title: 'Priority Support & SLA',
    desc: 'Named account manager, guaranteed response times, and onboarding assistance.',
  },
  {
    icon: 'Plug',
    title: 'API & Integrations',
    desc: 'Connect directly to your existing case management and claims platforms via REST API.',
  },
  {
    icon: 'GraduationCap',
    title: 'Custom Training',
    desc: 'Tailored onboarding sessions and ongoing training for your entire team.',
  },
];

const STATS = [
  { value: '15+', label: 'Report sections per case' },
  { value: '98%', label: 'Investigator satisfaction' },
  { value: '<2 min', label: 'Average report generation' },
  { value: '256-bit', label: 'Encryption standard' },
];

const EnterpriseSalesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userRole, signOut } = useAuth();

  const handleDashboard = () => {
    if (userRole === 'admin') navigate('/admin-dashboard');
    else if (userRole === 'expert') navigate('/expert-dashboard');
    else navigate('/client-dashboard');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    phone: '',
    estimatedMonthlyCases: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.fullName || !formData?.email || !formData?.estimatedMonthlyCases) {
      toast?.error('Please fill in all required fields');
      return;
    }
    try {
      setIsSubmitting(true);
      const sanitizedData = {
        fullName: sanitizeInput(formData?.fullName),
        email: sanitizeInput(formData?.email),
        companyName: sanitizeInput(formData?.companyName),
        phone: sanitizeInput(formData?.phone),
        estimatedMonthlyCases: parseInt(formData?.estimatedMonthlyCases),
        message: sanitizeInput(formData?.message),
      };
      const { error: insertError } = await supabase
        ?.from('enterprise_leads')
        ?.insert({
          full_name: sanitizedData?.fullName,
          email: sanitizedData?.email,
          company_name: sanitizedData?.companyName,
          phone: sanitizedData?.phone,
          estimated_monthly_cases: sanitizedData?.estimatedMonthlyCases,
          message: sanitizedData?.message,
          status: 'new',
          source: 'contact_sales_form',
        });
      if (insertError) throw insertError;
      const { error: emailError } = await supabase?.functions?.invoke('send-enterprise-lead-notification', {
        body: { leadId: null, contactData: sanitizedData },
      });
      if (emailError) console.error('Email notification error:', emailError);
      setSubmitted(true);
      toast?.success('Thank you! Our sales team will contact you shortly.');
    } catch (error) {
      console.error('Submit enterprise lead error:', error);
      toast?.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({ fullName: '', email: '', companyName: '', phone: '', estimatedMonthlyCases: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Top Nav ── */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
            </div>

            <nav className="hidden lg:flex items-center gap-8">
              <button
                onClick={() => navigate('/expert-marketplace-listing')}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Experts
              </button>
              <button
                onClick={() => navigate('/#pricing')}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Contact
              </button>
            </nav>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Button variant="default" size="default" onClick={handleDashboard}>
                    Go to Dashboard
                  </Button>
                  <Button variant="outline" size="default" iconName="LogOut" onClick={handleLogout}>
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={() => navigate('/login')}
                    className="hidden md:inline-flex"
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    size="default"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => navigate('/signup')}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide mb-6">
            <Icon name="Building2" size={14} />
            Enterprise &amp; High-Volume Teams
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-foreground leading-tight mb-5">
            Fire Investigation at Scale—<br className="hidden md:block" />
            Built for Your Organization
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Custom pricing, dedicated support, API access, and enterprise-grade security.
            Let's build a solution around your team's exact workflow.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['NFPA 921 Aligned', 'SOC 2-Ready Infrastructure', '256-bit Encryption', 'Custom SLA'].map(badge => (
              <span key={badge} className="flex items-center gap-1.5 px-3 py-1.5 bg-card border border-border rounded-full text-xs font-medium text-muted-foreground">
                <Icon name="CheckCircle" size={13} className="text-success" />
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Row ── */}
      <section className="border-b border-border bg-card/50">
        <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map(s => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold text-primary mb-1">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Two-column: features left, form right ── */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-start">

        {/* Left – feature list */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Everything your team needs
          </h2>
          <p className="text-muted-foreground mb-8">
            FireCaseIQ Enterprise is purpose-built for insurance carriers, fire departments,
            law firms, and forensic consulting firms that demand consistency and volume.
          </p>
          <div className="space-y-6">
            {FEATURES.map(f => (
              <div key={f.title} className="flex gap-4">
                <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={f.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-0.5">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Direct email strip */}
          <div className="mt-10 p-4 bg-muted/40 border border-border rounded-lg flex items-center gap-3">
            <Icon name="Mail" size={18} className="text-primary flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              Prefer email?{' '}
              <a
                href="mailto:sales@firecaseiq.com"
                className="text-primary font-medium hover:underline"
              >
                sales@firecaseiq.com
              </a>{' '}
              — we respond within one business day.
            </p>
          </div>
        </div>

        {/* Right – contact form */}
        <div className="md:sticky md:top-24">
          {submitted ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Request Received!</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Our enterprise sales team will reach out within 24 hours to discuss a custom plan for your organization.
              </p>
              <button
                onClick={resetForm}
                className="px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Submit Another Inquiry
              </button>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-1">Talk to Sales</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Fill in your details and we'll be in touch within one business day.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Full Name <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData?.fullName}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Work Email <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleChange}
                      placeholder="jane@company.com"
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Organization
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData?.companyName}
                      onChange={handleChange}
                      placeholder="Acme Insurance Co."
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Estimated Monthly Case Volume <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="estimatedMonthlyCases"
                    value={formData?.estimatedMonthlyCases}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    required
                  >
                    <option value="">Select volume range…</option>
                    <option value="50">50–100 cases / month</option>
                    <option value="100">100–250 cases / month</option>
                    <option value="250">250–500 cases / month</option>
                    <option value="500">500+ cases / month</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    Anything else we should know?
                  </label>
                  <textarea
                    name="message"
                    value={formData?.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Current tools, integration needs, timeline…"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={16} />
                      Request Enterprise Pricing
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-muted-foreground">
                  By submitting, you agree to our{' '}
                  <Link to="/privacy-policy" className="hover:underline text-primary/80">
                    Privacy Policy
                  </Link>
                  . No spam, ever.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ── Footer strip ── */}
      <footer className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} FireCaseIQ. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EnterpriseSalesPage;